import { MarketingConsent } from '@iheartjane/menu';
import {
  Flex,
  Link,
  Typography,
  emailRegex,
  telRegex,
} from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';

import { ContinueWith } from '../components/continueWith';

export interface CreateAccountProps {
  errors: string[] | null;

  isMarketplace: boolean;

  /** function to open Apple login when button is clicked */
  onAppleClick(): void;

  /** function to open Google login when button is clicked */
  onGoogleClick(): void;

  /** Function passed to determine what happens when sign in issubmitted */
  onSubmit(user: CreateAccountData): void;

  /** Boolean values checking Boost menu configuration */
  showAppleLogin: boolean;
  showGoogleLogin: boolean;
}

export type CreateAccountData = {
  email: string;
  password: string;
  phone: string;
};

export const CreateAccount = ({
  errors,
  isMarketplace,
  onSubmit,
  onAppleClick,
  onGoogleClick,
  showAppleLogin,
  showGoogleLogin,
}: CreateAccountProps) => {
  return (
    <Form name="sign-in" onSubmit={onSubmit}>
      <Flex width="100%" flexDirection="column" alignItems="center">
        {(showAppleLogin || showGoogleLogin) && (
          <ContinueWith
            onAppleClick={onAppleClick}
            onGoogleClick={onGoogleClick}
            showAppleLogin={showAppleLogin}
            showGoogleLogin={showGoogleLogin}
          />
        )}
        <Form.TextField
          type="email"
          autocomplete="off"
          label="Email"
          name="email"
          required
          requiredLabel
          requiredMessage="Please enter your email address."
          errorMessage="Please enter a valid email address (e.g., name@example.com)."
          errorOnBlur
          pattern={emailRegex}
          disableMobileInputStyling
          my={24}
        />
        <Form.NumberField
          data-testid="phone-number-field"
          width="100%"
          disableMobileInputStyling
          allowLeadingZeros
          autocomplete="off"
          label="Phone number"
          helperText="Used only for reservation notices"
          maskFormat="(###) ###-####"
          name="phone"
          pattern={telRegex}
          errorMessage="Please enter a valid 10-digit phone number (e.g., (555) 123-4567)."
          errorOnBlur
          required
          requiredLabel
          requiredMessage="Please enter your phone number."
          mb={24}
        />
        <Form.TextField
          type="password"
          autocomplete="off"
          label="Password"
          name="password"
          required
          requiredLabel
          requiredMessage="Please enter your password."
          errorOnBlur
          disableMobileInputStyling
          mb={24}
        />

        {isMarketplace && (
          <Flex mb={24} flexDirection="row">
            <Form.CheckboxField
              disableMobileInputStyling
              name="promotionsOptIn"
              label="Promotional emails"
              labelHidden
              defaultChecked
            />
            <MarketingConsent />
          </Flex>
        )}

        {errors && (
          <Flex flexDirection="column" justifyContent="center" mt={-12}>
            {Array.isArray(errors) ? (
              errors.map((error) => (
                <Typography key={error} color="error" mb={12} mx="auto">
                  {error}
                </Typography>
              ))
            ) : (
              <Typography color="error" mb={12} mx="auto">
                There was an error creating your account.
              </Typography>
            )}
          </Flex>
        )}

        <Form.SubmitButton
          full
          variant="primary"
          label="Sign up"
          mb={24}
          data-testid="login-submit-btn"
        />
        <Typography textAlign="center" color="grays-mid">
          By creating an account, I agree to{' '}
          <Link href="https://www.iheartjane.com/terms">
            Jane's Terms of Service
          </Link>{' '}
          and{' '}
          <Link href="https://www.iheartjane.com/privacy-policy">
            Privacy Policy
          </Link>
          .
        </Typography>
      </Flex>
    </Form>
  );
};
